import React from 'react';
import { Translate } from '@bynder/localization';
import { useFetchGuideById } from '../../queries/guides';
import Forbidden from '../errorPages/Forbidden';
import GuideNotFound from '../errorPages/GuideNotFound';
import Unauthorized from '../errorPages/Unauthorized';
import ScrollToTopButton from '../shared/ScrollToTopButton';
import Header from './Header';
import Page from '../pages/Page';
import GuideSidebar from './Sidebar';
import { GuideSkeleton } from './GuideSkeleton';
import { UserType, useUserType } from '../../helpers/hooks';
import RedirectToLogin from '../shared/RedirectToLogin';
import Unpublished from '../errorPages/Unpublished';
import { InspectorContainer } from './Inspector/InspectorContainer';
import { StyledGuideContainer, StyledGuideContent } from './Styles';
import { useGuideStore } from '../../stores/guideStore';
import EmptyGuide from './EmptyGuide';
import { useInspectorStore } from '../../stores/inspectorStore';
import * as GuideHooks from './hooks';
import * as LandingPageHooks from '../landingpage/hooks';
import { EntityVisibility, GuideType } from '../../types';
import { SinglePageHeader } from '../landingpage/SinglePageHeader';
import { useThemesetByGuideId } from '../../queries/themesets';
import AudiencesSidebar from '../landingpage/sidebar/AudienceSidebar';
import { ActiveSplits, useReleaseToggle } from '../../helpers/releaseToggle';
import InternalServerError from '../errorPages/InternalServerError';
import { useEntityPermission } from '../../queries/permissions';

const Guide: React.FC = () => {
  const activeGuideId = useGuideStore(s => s.activeGuideId);
  const activePageId = useGuideStore(s => s.activePageId);
  const isEditMode = useGuideStore(s => s.isEditMode);
  const isInspectorOpen = useInspectorStore(s => s.isInspectorOpen);
  const { userType } = useUserType();
  const { data: guidePermissions } = useEntityPermission(activeGuideId);

  const guideContentRef = React.useRef(null);
  const { data: isPageInPageEnabled } = useReleaseToggle(
    ActiveSplits.pageInPage,
  );

  const {
    data: guide,
    isLoading,
    isError,
    error,
  } = useFetchGuideById(activeGuideId);

  const isMultiPage = guide?.type === GuideType.MULTI_PAGE;
  const isPublicSinglePage =
    guide?.type === GuideType.SINGLE_PAGE &&
    guide?.visibility === EntityVisibility.PUBLIC;
  const { data: theme } = useThemesetByGuideId(activeGuideId);
  const isHeaderHidden =
    isPublicSinglePage && !theme?.properties?.landingPageHeaderEnabled;

  const { firstEditablePageId, firstViewablePageId } =
    GuideHooks.useRedirectToFirstViewablePage();
  GuideHooks.useSetActiveChapter();
  GuideHooks.useSetGuideVersion();
  GuideHooks.useOnlyAllowEditingInLaptops();
  GuideHooks.useGuideThemeset();
  GuideHooks.useGuideSizeLimit();
  GuideHooks.useHeaderVisibility();
  GuideHooks.useResetScroll(guideContentRef);
  GuideHooks.useGoogleAnalyticsTrackNavigation();
  GuideHooks.useClearStoreOnUnmountGuide();
  LandingPageHooks.useHideMainHeader(isHeaderHidden);
  LandingPageHooks.useDocumentTitle();
  GuideHooks.useRedirectToDefaultPage();
  GuideHooks.useManagerNavigation();

  const isGuideEmptyInViewMode = () => {
    const guideHasNoChapters = guide?.chapters.length === 0;
    const guideHasNoViewablePages = !firstViewablePageId;
    const guideHasNoEditablePages = !firstEditablePageId;

    return (
      (guideHasNoChapters ||
        (guideHasNoViewablePages && guideHasNoEditablePages)) &&
      !isEditMode
    );
  };

  const renderSidebar = () => {
    if (isMultiPage) {
      return <GuideSidebar guide={guide} />;
    }

    if (
      guide &&
      guide.type === GuideType.HOME_PAGE &&
      guide?.targetedPagesEnabled &&
      isPageInPageEnabled &&
      isEditMode
    ) {
      return <AudiencesSidebar guide={guide} />;
    }

    return null;
  };
  if (isError) {
    if (userType === UserType.OPEN_ASSET_BANK || userType === UserType.PUBLIC) {
      return <RedirectToLogin />;
    }

    switch (error?.response?.status) {
      case 401:
        return (
          <Unauthorized
            errorHeader={<Translate id="UNAUTHORIZED.HEADER_TEXT" />}
            errorMessage={<Translate id="UNAUTHORIZED.PARAGRAPH_TEXT_1" />}
          />
        );
      case 403:
        return <Forbidden documentType="guide" />;
      case 404:
        return <GuideNotFound />;
      default:
        return <InternalServerError />;
    }
  }

  if (isLoading) return <GuideSkeleton />;

  if (isGuideEmptyInViewMode()) {
    return <Unpublished canEdit={guide?.canEdit} emptyGuide />;
  }

  return (
    <>
      {isMultiPage ? (
        <Header
          guideId={guide.id}
          isGuideShared={guidePermissions?.permissions.length > 0}
          guideVisibility={guide.visibility}
          guideTitle={guide.title}
          guideOwnerId={guide.ownerId}
          canEditGuide={guide.canEdit}
          guideGroupId={guide.groupId}
          guideType={guide.type}
        />
      ) : (
        <SinglePageHeader
          isHeaderHidden={isHeaderHidden}
          guideGroupId={guide.groupId}
          guideId={guide.id}
        />
      )}
      <StyledGuideContainer
        $guideType={guide.type}
        $isHeaderHidden={isHeaderHidden}
      >
        {renderSidebar()}
        <StyledGuideContent ref={guideContentRef} $isOpen={isInspectorOpen}>
          {activePageId ? (
            <Page
              guideType={guide.type}
              activeGuideId={guide.id}
              activePageId={activePageId}
            />
          ) : (
            <EmptyGuide />
          )}
          {!isEditMode && <ScrollToTopButton containerRef={guideContentRef} />}
        </StyledGuideContent>
        {isEditMode && <InspectorContainer />}
      </StyledGuideContainer>
    </>
  );
};

export default Guide;
